import { ErrorMessage } from '@hookform/error-message'
import {
  IonLabel,
  IonItem,
  IonInput,
  IonButton,
  InputCustomEvent,
  IonDatetime,
  DatetimeCustomEvent,
  IonInputPasswordToggle,
} from '@ionic/react'
import React, { useRef } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import parsePhoneNumber from 'libphonenumber-js'
import { isBefore, sub } from 'date-fns'

import { useAppDispatch } from '../../../hooks/reduxHooks'
import { AuthState, setSignUpData } from '../../../store/redux/slices/authSlice'
import { useNativeKeyboard } from '../../../hooks/useNativeKeyboard'
import { useSelector } from 'react-redux'
import { AppState } from '../../../store/redux/types'
import { validateSpanishDNI } from '../../../utils/validateSpanishDNI'
import dateToCustomString from '../../../utils/dateToCustomString'
import { formatPhoneNumber } from '../../../utils/formatPhoneNumber'
import { RegionState } from '../../../store/redux/slices/regionSlice'
import { ConfigState } from '../../../store/redux/slices/configSlice'
import useIsSpain from '../../../hooks/useIsSpain'
import useFetchConfig from '../../../hooks/useFetchConfig'
import styles from '../styles/SignUpPage.module.scss'

type UserDetailsFormProps = {
  nextStep: () => void
}

interface UserDetailsFormInputs {
  firstname: string
  lastname: string
  birthdate: string
  id: string
  mobile: string
  email: string
  password: string
  confirmPassword: string
}

const UserDetailsForm: React.FC<UserDetailsFormProps> = ({ nextStep }) => {
  const dispatch = useAppDispatch()
  const { signUpData } = useSelector((state: AppState): AuthState => state.auth)
  const { selectedLanguage, apiCode } = useSelector((state: AppState): RegionState => state.region)
  const { config } = useSelector((state: AppState): ConfigState => state.config)
  const { t } = useTranslation()
  const handleNativeKeyboard = useNativeKeyboard()
  const isSpain = useIsSpain()
  useFetchConfig()
  const minAge = config[apiCode]?.minAge ?? 18

  const {
    handleSubmit,
    control,
    watch,
    formState: { errors },
  } = useForm<UserDetailsFormInputs>({
    defaultValues: {
      firstname: signUpData?.firstname || '',
      lastname: signUpData?.lastname || '',
      birthdate: signUpData?.birthdate || '',
      id: signUpData?.id || '',
      mobile: signUpData?.mobile || '',
      email: signUpData?.email || '',
      password: signUpData?.password || '',
      confirmPassword: signUpData?.password || '',
    },
  })
  const password = useRef<string | null | undefined>('')
  password.current = watch('password', '')

  const submitHandler = async (data: UserDetailsFormInputs) => {
    const { firstname, lastname, birthdate, id, mobile, email, password } = data
    const newMobile = formatPhoneNumber(mobile)
    dispatch(setSignUpData({ firstname, lastname, birthdate, id, mobile: newMobile, email, password }))
    nextStep()
  }

  return (
    <>
      <h1 className="ion-padding-vertical">{`${t('signup.personalInformation')} - ${t('signup.step1')}`}</h1>
      <form onSubmit={handleSubmit(submitHandler)} className={styles.DetailsUserForm}>
        <IonItem className="ion-no-padding">
          <Controller
            control={control}
            name="firstname"
            render={({ field: { onChange, onBlur, value } }) => (
              <IonItem>
                <IonLabel position="stacked">
                  {t('forms.firstName')}{' '}
                  <span style={{ color: 'var(--ion-color-primary)' }}>({t('forms.requiredField')})</span>
                </IonLabel>
                <IonInput
                  onBlur={onBlur}
                  onIonChange={(e) => onChange(e.detail.value ?? '')}
                  onIonFocus={handleNativeKeyboard}
                  autocapitalize="on"
                  placeholder={t('forms.placeholder.firstName')}
                  enterkeyhint="next"
                  required={true}
                  inputMode="text"
                  value={value ?? ''}
                />
              </IonItem>
            )}
            rules={{
              required: `${t('forms.required')}`,
              pattern: {
                // eslint-disable-next-line no-misleading-character-class
                value: /^[\p{L} -j́]*$/gu,
                message: t('forms.invalidFirstName'),
              },
            }}
          />
        </IonItem>
        <ErrorMessage
          errors={errors}
          name="firstname"
          as={<IonLabel className="validationErrorMsg ion-text-wrap" color="danger" />}
        />
        <IonItem className="ion-no-padding">
          <Controller
            control={control}
            name="lastname"
            render={({ field: { onChange, onBlur, value } }) => (
              <IonItem>
                <IonLabel position="stacked">
                  {t('forms.lastNames')}{' '}
                  <span style={{ color: 'var(--ion-color-primary)' }}>({t('forms.requiredField')})</span>
                </IonLabel>
                <IonInput
                  onBlur={onBlur}
                  onIonChange={(e) => onChange(e.detail.value ?? '')}
                  onIonFocus={handleNativeKeyboard}
                  autocapitalize="on"
                  placeholder={t('forms.placeholder.lastNames')}
                  enterkeyhint="next"
                  required={true}
                  inputMode="text"
                  value={value ?? ''}
                />
              </IonItem>
            )}
            rules={{
              required: `${t('forms.required')}`,
              pattern: {
                // eslint-disable-next-line no-misleading-character-class
                value: /^[\p{L} -j́]*$/gu,
                message: t('forms.invalidLastNames'),
              },
            }}
          />
        </IonItem>
        <ErrorMessage
          errors={errors}
          name="lastname"
          as={<IonLabel className="validationErrorMsg ion-text-wrap" color="danger" />}
        />
        <IonItem className="ion-no-padding">
          <Controller
            control={control}
            name="birthdate"
            render={({ field: { onChange, onBlur, value } }) => (
              <IonDatetime
                color="primary"
                presentation="date"
                size="cover"
                onBlur={onBlur}
                onIonChange={(event: DatetimeCustomEvent) => {
                  const dateSelected = event.detail.value!.toString()
                  onChange(dateToCustomString(new Date(dateSelected), 'yyyy-mm-dd'))
                }}
                onIonFocus={handleNativeKeyboard}
                locale={selectedLanguage || 'en-GB'}
                value={value ?? ''}
              >
                <span slot="title">
                  {`${t('forms.birthdate')}`}{' '}
                  <span style={{ fontSize: '0.5rem', paddingLeft: '1%' }}>({t('forms.requiredField')}) </span>
                </span>
              </IonDatetime>
            )}
            rules={{
              required: `${t('forms.required')}`,
              validate: (value) => {
                if (!value) {
                  return t('forms.required')
                }
                const birthdate = new Date(value)
                const nowMinusMinAge = sub(new Date(), { years: minAge })
                if (isBefore(birthdate, nowMinusMinAge)) {
                  return true
                }
                return t('forms.olderThan', { age: minAge })
              },
            }}
          />
        </IonItem>
        <ErrorMessage
          errors={errors}
          name="birthdate"
          as={<IonLabel className="validationErrorMsg ion-text-wrap" color="danger" />}
        />
        {isSpain && (
          <span>
            <IonItem className="ion-no-padding">
              <Controller
                control={control}
                name="id"
                render={({ field: { onChange, onBlur, value } }) => (
                  <IonItem>
                    <IonLabel position="stacked">
                      {t('forms.idNumber')}{' '}
                      {isSpain && (
                        <span style={{ color: 'var(--ion-color-primary)' }}>({t('forms.requiredField')})</span>
                      )}
                    </IonLabel>
                    <IonInput
                      type="text"
                      onBlur={onBlur}
                      onIonChange={(e) => onChange(e.detail.value ?? '')}
                      onIonFocus={handleNativeKeyboard}
                      placeholder={isSpain ? '12345678R' : ''}
                      enterkeyhint="next"
                      autocapitalize="characters"
                      inputMode="text"
                      required={isSpain}
                      value={value ?? ''}
                    />
                  </IonItem>
                )}
                rules={{
                  required: isSpain ? `${t('forms.required')}` : false,
                  validate: (value) => {
                    if (isSpain) {
                      if (!value) {
                        return t('forms.required')
                      }
                      if (!validateSpanishDNI(value)) {
                        return t('forms.invalidId')
                      }
                    }
                    return true
                  },
                }}
              />
            </IonItem>
            <ErrorMessage
              errors={errors}
              name="id"
              as={<IonLabel className="validationErrorMsg ion-text-wrap" color="danger" />}
            />
          </span>
        )}

        <IonItem className="ion-no-padding">
          <Controller
            control={control}
            name="mobile"
            render={({ field: { onChange, onBlur, value } }) => (
              <IonItem>
                <IonLabel position="stacked">
                  {t('forms.phoneNumber')}{' '}
                  <span style={{ color: 'var(--ion-color-primary)' }}>({t('forms.requiredFieldPhone')})</span>
                </IonLabel>
                <IonInput
                  type="tel"
                  onBlur={onBlur}
                  onIonChange={(e) => onChange(e.detail.value ?? '')}
                  onIonFocus={handleNativeKeyboard}
                  placeholder={t('forms.placeholder.phoneNumber')}
                  enterkeyhint="next"
                  autocapitalize="off"
                  inputMode="tel"
                  required={true}
                  value={value ?? ''}
                />
              </IonItem>
            )}
            rules={{
              required: `${t('forms.required')}`,
              validate: (value) => {
                if (!value) {
                  return t('forms.required')
                }
                const formattedPhone = parsePhoneNumber(value)
                if (!formattedPhone || !formattedPhone.isValid()) {
                  return t('forms.invalidPhoneNumber')
                }
                return true
              },
            }}
          />
        </IonItem>
        <ErrorMessage
          errors={errors}
          name="mobile"
          as={<IonLabel className="validationErrorMsg ion-text-wrap" color="danger" />}
        />
        <IonItem className="ion-no-padding">
          <Controller
            control={control}
            name="email"
            render={({ field: { onChange, onBlur, value } }) => (
              <IonItem>
                <IonLabel position="stacked">
                  {t('forms.emailAddress')}{' '}
                  <span style={{ color: 'var(--ion-color-primary)' }}>({t('forms.requiredField')})</span>
                </IonLabel>
                <IonInput
                  type="email"
                  onBlur={onBlur}
                  onIonChange={(e) => onChange(e.detail.value ?? '')}
                  onIonFocus={handleNativeKeyboard}
                  placeholder={t('forms.placeholder.email')}
                  enterkeyhint="next"
                  autocapitalize="off"
                  inputMode="email"
                  required={true}
                  value={value ?? ''}
                />
              </IonItem>
            )}
            rules={{
              required: `${t('forms.required')}`,
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,24}$/i,
                message: t('forms.invalidEmail'),
              },
            }}
          />
        </IonItem>
        <ErrorMessage
          errors={errors}
          name="email"
          as={<IonLabel className="validationErrorMsg ion-text-wrap" color="danger" />}
        />
        <IonItem className="ion-no-padding">
          <Controller
            control={control}
            name="password"
            render={({ field: { onChange, onBlur, value } }) => (
              <IonItem>
                <IonLabel position="stacked">
                  {t('forms.password')}{' '}
                  <span style={{ color: 'var(--ion-color-primary)' }}>({t('forms.requiredField')})</span>
                </IonLabel>
                <IonInput
                  type="password"
                  onBlur={onBlur}
                  onIonChange={(event: InputCustomEvent) => {
                    onChange(event.detail.value)
                    password.current = event.detail.value
                  }}
                  onIonFocus={handleNativeKeyboard}
                  placeholder={t('forms.enterPassword')}
                  enterkeyhint="next"
                  required={true}
                  inputMode="text"
                  value={value ?? ''}
                >
                  <IonInputPasswordToggle slot="end"></IonInputPasswordToggle>
                </IonInput>
              </IonItem>
            )}
            rules={{
              required: `${t('forms.required')}`,
              pattern: {
                value: /^(?=.*[a-z])(?=.*\d)[a-zA-Z\w\W]{8,}$/,
                message: t('forms.invalidPassword'),
              },
            }}
          />
        </IonItem>
        <ErrorMessage
          errors={errors}
          name="password"
          as={<IonLabel className="validationErrorMsg ion-text-wrap" color="danger" />}
        />
        <IonItem className="ion-no-padding">
          <Controller
            control={control}
            name="confirmPassword"
            render={({ field: { onChange, onBlur, value } }) => (
              <IonItem>
                <IonLabel position="stacked">
                  {t('forms.confirmPassword')}{' '}
                  <span style={{ color: 'var(--ion-color-primary)' }}>({t('forms.requiredField')})</span>
                </IonLabel>
                <IonInput
                  type="password"
                  onBlur={onBlur}
                  onIonChange={(e) => onChange(e.detail.value ?? '')}
                  onIonFocus={handleNativeKeyboard}
                  placeholder={t('forms.confirmPassword')}
                  enterkeyhint="send"
                  required={true}
                  inputMode="text"
                  value={value ?? ''}
                >
                  <IonInputPasswordToggle slot="end"></IonInputPasswordToggle>
                </IonInput>
              </IonItem>
            )}
            rules={{
              required: `${t('forms.required')}`,
              validate: (value): boolean => {
                return value === watch('password')
              },
            }}
          />
        </IonItem>
        <ErrorMessage
          errors={errors}
          message={t('login.unmatchingPasswords')}
          name="confirmPassword"
          as={<IonLabel className="validationErrorMsg ion-text-wrap" color="danger" />}
        />
        <IonButton className="ion-margin-top" expand="block" type="submit">
          {t('btn.next')}
        </IonButton>
      </form>
    </>
  )
}

export default UserDetailsForm
